import { useCallback } from 'react';
import {
  RunResponse,
  RunResponseContent,
} from '@/features/ai-agents/shared/models';

/**
 * Processes a single JSON chunk by passing it to the provided callback.
 * @param chunk - A parsed JSON object of type RunResponse.
 * @param onChunk - Callback to handle the chunk.
 */
function processChunk(
  chunk: RunResponse,
  onChunk: (chunk: RunResponse) => void,
) {
  onChunk(chunk);
}

/**
 * Detects if the incoming data is in the legacy format (direct RunResponseContent)
 * @param data - The parsed data object
 * @returns true if it's in the legacy format, false if it's in the new format
 */
function isLegacyFormat(data: RunResponseContent): boolean {
  return (
    typeof data === `object` &&
    data !== null &&
    `event` in data &&
    !(`data` in data) &&
    typeof data.event === `string`
  );
}

interface NewFormatData {
  event: string;
  data: string | Record<string, unknown>;
}

type LegacyEventFormat = RunResponseContent & { event: string };

function convertNewFormatToLegacy(
  newFormatData: NewFormatData,
): LegacyEventFormat {
  const { event, data } = newFormatData;

  // Parse the data field if it's a string
  let parsedData: Record<string, unknown>;
  if (typeof data === `string`) {
    try {
      // First try to parse as JSON
      parsedData = JSON.parse(data);
    } catch {
      parsedData = {};
    }
  } else {
    parsedData = data;
  }

  const { ...cleanData } = parsedData;

  // Convert to legacy format by flattening the structure
  return {
    event,
    ...cleanData,
  } as LegacyEventFormat;
}

/**
 * Parses a string buffer to extract complete JSON objects.
 *
 * This function discards any extraneous data before the first '{', then
 * repeatedly finds and processes complete JSON objects.
 *
 * @param text - The accumulated string buffer.
 * @param onChunk - Callback to process each parsed JSON object.
 * @returns Remaining string that did not form a complete JSON object.
 */
/**
 * Extracts complete JSON objects from a buffer string **incrementally**.
 * - It allows partial JSON objects to accumulate across chunks.
 * - It ensures real-time streaming updates.
 */
function parseBuffer(
  buffer: string,
  onChunk: (chunk: RunResponse) => void,
): string {
  let currentIndex = 0;
  let jsonStartIndex = buffer.indexOf(`{`, currentIndex);

  // Process as many complete JSON objects as possible.
  while (jsonStartIndex !== -1 && jsonStartIndex < buffer.length) {
    let braceCount = 0;
    let inString = false;
    let escapeNext = false;
    let jsonEndIndex = -1;
    let i = jsonStartIndex;

    // Walk through the string to find the matching closing brace.
    for (; i < buffer.length; i += 1) {
      const char = buffer[i];

      if (inString) {
        if (escapeNext) {
          escapeNext = false;
        } else if (char === `\\`) {
          escapeNext = true;
        } else if (char === `"`) {
          inString = false;
        }
      } else if (char === `"`) {
        inString = true;
      } else if (char === `{`) {
        braceCount += 1;
      } else if (char === `}`) {
        braceCount -= 1;
        if (braceCount === 0) {
          jsonEndIndex = i;
          break;
        }
      }
    }

    // If we found a complete JSON object, try to parse it.
    if (jsonEndIndex !== -1) {
      const jsonString = buffer.slice(jsonStartIndex, jsonEndIndex + 1);

      try {
        const parsed = JSON.parse(jsonString);

        // Check if it's in the legacy format - use as is
        if (isLegacyFormat(parsed)) {
          processChunk(parsed, onChunk);
        } else {
          // New format - convert to legacy format for compatibility
          const legacyChunk = convertNewFormatToLegacy(parsed);
          processChunk(legacyChunk, onChunk);
        }
      } catch {
        // Move past the starting brace to avoid re-parsing the same invalid JSON.
        jsonStartIndex = buffer.indexOf(`{`, jsonStartIndex + 1);
        /* eslint-disable-next-line no-continue */
        continue;
      }

      // Move currentIndex past the parsed JSON and trim any leading whitespace.
      currentIndex = jsonEndIndex + 1;
      buffer = buffer.slice(currentIndex).trim();

      // Reset currentIndex and search for the next JSON object.
      currentIndex = 0;
      jsonStartIndex = buffer.indexOf(`{`, currentIndex);
    } else {
      // If a complete JSON object is not found, break out and wait for more data.
      break;
    }
  }

  // Return any unprocessed (partial) data.
  return buffer;
}

function formatErrorMessage(error: unknown): string {
  let errorMessage = ``;

  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === `object` && error !== null && `detail` in error) {
    errorMessage = String(error?.[`detail`] ?? error);
  } else {
    errorMessage = String(error);
  }

  if (
    errorMessage.includes(`Part exceeded maximum size`) ||
    errorMessage.includes(`1024KB`)
  ) {
    errorMessage = `Section is too big, try to split the section into smaller parts and resend.`;
  }

  return `Agent returned with error: ${errorMessage}`;
}

/**
 * Custom React hook to handle streaming API responses as JSON objects.
 *
 * This hook:
 * - Accumulates partial JSON data from streaming responses.
 * - Extracts complete JSON objects and processes them via onChunk.
 * - Handles errors via onError and signals completion with onComplete.
 *
 * @returns An object containing the streamResponse function.
 */
export function useAIResponseStream() {
  const streamResponse = useCallback(
    async (options: {
      apiUrl: string;
      headers?: Record<string, string>;
      requestBody: FormData | Record<string, unknown>;
      onChunk: (chunk: RunResponse) => void;
      onError: (error: Error) => void;
      onComplete: () => void;
      abortController?: AbortController;
    }): Promise<void> => {
      const {
        apiUrl,
        headers = {},
        requestBody,
        onChunk,
        onError,
        onComplete,
        abortController,
      } = options;

      // Buffer to accumulate partial JSON data.
      let buffer = ``;

      try {
        const response = await fetch(apiUrl, {
          method: `POST`,
          signal: abortController?.signal,
          headers: {
            'Cache-Control': `no-cache`,
            Connection: `keep-alive`,
            'Transfer-Encoding': `chunked`,
            ...(!(requestBody instanceof FormData) && {
              'Content-Type': `application/json`,
            }),
            ...headers,
          },
          body:
            requestBody instanceof FormData
              ? requestBody
              : JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw errorData;
        }

        if (!response.body) {
          throw new Error(`No response body`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        // Recursively process the stream.
        const processStream = async (): Promise<void> => {
          const { done, value } = await reader.read();
          if (done) {
            // Process any final data in the buffer.
            buffer = parseBuffer(buffer, onChunk);
            onComplete();
            return;
          }
          // Decode, sanitize, and accumulate the chunk
          buffer += decoder.decode(value, { stream: true });

          // Parse any complete JSON objects available in the buffer.
          buffer = parseBuffer(buffer, onChunk);
          await processStream();
        };
        await processStream();
      } catch (error) {
        onError(new Error(formatErrorMessage(error)));
      }
    },
    [],
  );

  return { streamResponse };
}
